import { Injectable } from "@angular/core";
import { NavigationExtras, Params, RouterState } from "@angular/router";
import { ProjectSelectors } from "@app/project/store/selectors";
import { Store, select } from "@ngrx/store";
import { combineLatest } from "rxjs";
import { map, take, tap } from "rxjs/operators";
import * as RouterActions from "./actions";
import { RouterSelectors } from "./selectors";

@Injectable({
    providedIn: "root",
})
export class RouterService {
    constructor(private readonly store: Store<RouterState>) {}

    navigateToUrl(url: string[], extras?: Omit<NavigationExtras, "queryParams">, query?: Params) {
        this.store.dispatch(RouterActions.routerGo({ path: url, extras, query }));
    }

    selectRoute() {
        return this.store.pipe(select(RouterSelectors.getUrl), take(1));
    }

    getRouteState() {
        return this.store.pipe(select(RouterSelectors.getRouterState));
    }

    getPath() {
        return this.getRouteState().pipe(map(state => state.state.url.split("?")[0]));
    }

    navigateToCurrentUrlAndKeepQueryParams(category?: string[]) {
        return combineLatest([this.getQueryParams(), this.getRouteState()]).pipe(
            take(1),
            tap(([queryParams, routeState]) => {
                // This strips the query params from the current url and navigates to the url with the query params separate of url
                const redirectUrl = routeState.state.url.includes("?")
                    ? routeState.state.url.substring(0, routeState.state.url.indexOf("?"))
                    : routeState.state.url;
                this.navigateToUrl([redirectUrl, ...category], null, queryParams);
            })
        );
    }

    goBack() {
        this.store.dispatch(RouterActions.routerBack());
    }

    getUuid() {
        return this.store.pipe(select(ProjectSelectors.getUuid));
    }

    findParamFactory(param: string) {
        return this.store.pipe(select(RouterSelectors.selectUrlParam(param)));
    }

    getQueryParams() {
        return this.store.pipe(select(RouterSelectors.getQueryParams));
    }

    updatePageTitle(title) {
        this.store.dispatch(RouterActions.updatePageTitle({ title }));
    }

    getPageTitle() {
        return this.store.pipe(select(RouterSelectors.getTitle));
    }

    getBackAction() {
        return this.store.pipe(select(RouterSelectors.getBackAction));
    }

    updateBackButton(backButton) {
        this.store.dispatch(RouterActions.updateBackButton(backButton));
    }

    getChildRoute(route) {
        if (route && route.children && route.children[0]) {
            return this.getChildRoute(route.children[0]);
        }

        return route;
    }

    updateActiveRoutes() {
        return this.store.pipe(
            select(RouterSelectors.getRouterState),
            tap(routeState => {
                if (routeState) {
                    const state = routeState.state;
                    const activeRoute = this.getChildRoute(state);
                    this.store.dispatch(
                        RouterActions.updateActiveRoute({
                            url: activeRoute.url,
                            queryParams: activeRoute.queryParams,
                            params: activeRoute.params,
                            children: activeRoute.children,
                            title: activeRoute.title,
                        })
                    );
                }
            })
        );
    }

    updateQueryParams(queryParams: Params) {
        return this.getPath().pipe(
            take(1),
            tap(path => this.navigateToUrl([path], { replaceUrl: true }, queryParams))
        );
    }
}
